import React from 'react';
import { Link } from 'react-router-dom';

const Features: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23EF5000%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
        
        <div className="relative px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 text-5xl font-bold leading-tight text-white sm:text-6xl">
              Powerful <span className="bg-gradient-to-r from-primary-400 to-orange-400 bg-clip-text text-transparent">Features</span>
            </h1>
            <p className="max-w-2xl mx-auto mb-10 text-xl text-gray-300 leading-relaxed">
              Everything you need to create, manage, and analyze short URLs for your IoT devices and applications.
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="relative py-24 bg-gray-800/30 backdrop-blur-sm">
        <div className="px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2 xl:grid-cols-3">
            
            {/* Eddystone Optimization */}
            <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
              <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-primary-500 to-orange-500 rounded-macos-lg shadow-macos">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Eddystone Optimized</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                URLs automatically optimized for Eddystone beacon 16-byte constraints. Perfect encoding for maximum compatibility.
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• 16-byte URL optimization</li>
                <li>• Automatic encoding validation</li>
                <li>• Beacon compatibility testing</li>
              </ul>
            </div>

            {/* Advanced Analytics */}
            <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
              <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-macos-lg shadow-macos">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Advanced Analytics</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Detailed click analytics, geographic data, and device insights for comprehensive tracking.
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• Real-time click tracking</li>
                <li>• Geographic analytics</li>
                <li>• Device & browser insights</li>
              </ul>
            </div>

            {/* RESTful API */}
            <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
              <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-macos-lg shadow-macos">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Powerful API</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                RESTful API with authentication, rate limiting, and comprehensive documentation.
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• RESTful endpoints</li>
                <li>• API key authentication</li>
                <li>• Rate limiting & quotas</li>
              </ul>
            </div>

            {/* Custom Domains */}
            <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
              <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-macos-lg shadow-macos">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Custom Domains</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Use your own domain for branded short URLs with SSL certificates included.
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• Custom domain support</li>
                <li>• SSL certificates included</li>
                <li>• Brand consistency</li>
              </ul>
            </div>

            {/* Bulk Operations */}
            <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
              <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-macos-lg shadow-macos">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a2 2 0 002 2h8a2 2 0 002-2V3a2 2 0 012 2v6h-3a3 3 0 00-3 3v3H6a2 2 0 01-2-2V5zm11 2H5v7h10V7z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Bulk Operations</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Create, manage, and analyze thousands of URLs with bulk import and export capabilities.
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• CSV import/export</li>
                <li>• Batch URL creation</li>
                <li>• Mass analytics export</li>
              </ul>
            </div>

            {/* Security & Privacy */}
            <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
              <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-red-500 to-pink-500 rounded-macos-lg shadow-macos">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Security & Privacy</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Enterprise-grade security with password protection, expiration dates, and privacy controls.
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• Password protection</li>
                <li>• Link expiration</li>
                <li>• Privacy controls</li>
              </ul>
            </div>

          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-24">
        <div className="px-4 mx-auto text-center sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="mb-6 text-3xl font-bold text-white sm:text-4xl">
              Ready to get started?
            </h2>
            <p className="mb-10 text-lg text-gray-300">
              Experience all these features with our free plan or upgrade for advanced capabilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all duration-300 bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos-lg hover:from-primary-600 hover:to-orange-600 shadow-macos-lg hover:shadow-macos-xl hover:scale-105"
              >
                <span className="relative z-10">Start Free Trial</span>
                <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-gray-300 transition-all duration-300 border border-gray-600 rounded-macos-lg hover:text-white hover:border-gray-500 hover:bg-gray-800/50 backdrop-blur-sm"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;